import { Schema } from "effect"
import { GraphCommand, OptionEffect } from "../commands/graph"
import { Actor, ContentMeta, EntityRef, Evidence, RecordMeta, Reversibility, Risk, Tier } from "../common"
import {
  AgentId,
  ArtifactId,
  ChangesetId,
  CommentId,
  IntegrationId,
  Markdown,
  MemoryItemId,
  Millis,
  ProposalId,
  RunId,
  Seq,
  Sha256,
  ShortRef,
  TaskId,
  UserId,
} from "../ids"
import { Requires } from "../placement"
import { MemoryDraft, MemoryScope } from "./knowledge"

// #region proposal
export const QuestionOption = Schema.Struct({
  key: Schema.String,
  label: Schema.String,
  detail: Schema.NullOr(Schema.String),
  /** Typed effects, re-decided against current state when chosen, with the deciding human as actor. */
  effects: Schema.Array(OptionEffect),
})

/** Why a system process or agent needs a person. System escalations only offer flow effects (retry, reassign, cancel…). */
export const EscalationCause = Schema.Literals([
  "auth",
  "repeated_failure",
  "non_retryable",
  "review_rounds_exhausted",
  "stall",
  "loop",
  "spec_conflict",
  "runner_unavailable",
  "unreported",
])

/** One ledger, five kinds. Every surface (ghost nodes, Needs-you, chat cards, push) renders the same record by id. */
export const ProposalBody = Schema.TaggedUnion({
  /** Ghost nodes. Also covers hire agent, new area, abandon initiative, KPI target changes, reverts and undos. */
  plan_change: {
    changesetId: ChangesetId,
    rationale: Markdown,
    changes: Schema.NonEmptyArray(GraphCommand), // carry real, client-minted ids: ghosts become real in place
    baseSeq: Seq,
    revertOf: Schema.NullOr(Schema.Int), // plan version this restores
    undoOf: Schema.NullOr(ChangesetId),
  },
  /** To a person: up to 8 typed options, optional free text, a deadline. Escalations are questions with a cause. */
  question: {
    prompt: Markdown,
    subject: Schema.NullOr(EntityRef),
    options: Schema.Array(QuestionOption).check(Schema.isMaxLength(8)),
    allowFreeText: Schema.Boolean,
    defaultOnExpiry: Schema.NullOr(Schema.String), // option key; null = expires as "no answer"
    answersAsk: Schema.NullOr(CommentId), // an agent escalating an ask it can't answer
    escalation: Schema.NullOr(Schema.Struct({ cause: EscalationCause, detail: Markdown })),
  },
  /** Post, publish, submit, charge. Approval binds the exact arguments and the pinned tool schema. Phase 2. */
  outward_action: {
    taskId: TaskId, // a `publish` task
    integrationId: IntegrationId,
    tool: Schema.String,
    args: Schema.Json,
    argsHash: Sha256,
    toolSchemaHash: Sha256,
    preview: Schema.Struct({
      summary: Schema.String,
      recipient: Schema.NullOr(Schema.String),
      amount: Schema.NullOr(Schema.String),
      text: Schema.NullOr(Markdown),
      diffArtifactId: Schema.NullOr(ArtifactId),
    }),
    executor: Schema.NullOr(Requires), // stdio integrations run on a machine (asc-mcp → the Mac)
  },
  /** A CLI permission prompt in a run (permission.request). Decided by the run's principal only. */
  permission: {
    runId: RunId,
    requestId: Schema.String,
    tool: Schema.String,
    title: Schema.String,
    inputPreview: Schema.String, // redacted on the runner first
    inputHash: Sha256, // the decision binds to it
  },
  /** Memory create/revise/archive/promote. Applied in the scope owner's DO (via outbox for user/workspace scope). */
  memory: {
    op: Schema.Literals(["create", "revise", "archive", "promote"]),
    itemId: MemoryItemId,
    draft: Schema.NullOr(MemoryDraft),
    fromScope: Schema.NullOr(MemoryScope), // promote
    evidence: Schema.Array(Evidence),
    sourceRunId: Schema.NullOr(RunId),
  },
})
export type ProposalBody = typeof ProposalBody.Type

/** Who decides. Agents never decide their own proposals, nor any outward, permission, policy, grant or tainted one. */
export const Approver = Schema.TaggedUnion({
  /** A specific person (workspace owners/admins may also decide). */
  human: { userId: UserId },
  /** The project lead: low-risk untainted memory, and adopting a Mayor suggestion (re-filed under its own policy). */
  lead: { agentId: AgentId, escalateTo: UserId },
})

export const ProposalState = Schema.Literals([
  "open",
  "revision_requested", // back to the proposer; ReviseProposal re-opens it with revision + 1
  "approved", // outward_action only: approved, waiting for its publish run
  "applied", // plan/memory applied, permission delivered, outward executed
  "apply_failed", // re-decide failed against current state (stale); proposer woken
  "rejected",
  "answered", // question
  "expired", // = denied, or the question's default option was applied
  "withdrawn",
  "adopted", // the lead re-filed a Mayor suggestion as its own command
])

export const Proposal = Schema.Struct({
  ...RecordMeta, // ownerId = the person the proposal is for (routing)
  ...ContentMeta,
  id: ProposalId,
  ref: ShortRef, // P-58
  body: ProposalBody,
  revision: Schema.Int,
  /** sha256 over the canonical encoded body. DecideProposal must echo revision + contentHash (no TOCTOU). */
  contentHash: Sha256,
  proposedBy: Actor,
  reasoning: Markdown, // shown on the ghost node and the inbox card
  oneLine: Schema.String, // inbox headline: "why now"
  risk: Risk,
  reversibility: Reversibility,
  tier: Tier,
  blocks: Schema.Array(EntityRef), // what waits on it; a blocked task's readiness is `waiting_decision`
  tainted: Schema.Boolean, // most tainted input of the proposing run; tainted ⇒ approver is a human
  approver: Approver,
  decideBy: Schema.NullOr(Millis), // soft deadline shown in the inbox
  expiresAt: Schema.NullOr(Millis), // hard; expiry = denied
  state: ProposalState,
  perChange: Schema.Array(Schema.Literals(["pending", "accepted", "rejected"])), // plan_change only, index-aligned
  resolution: Schema.NullOr(
    Schema.Struct({
      by: Actor,
      at: Millis,
      optionKey: Schema.NullOr(Schema.String),
      text: Schema.NullOr(Schema.String),
      note: Schema.NullOr(Schema.String),
      changesetId: Schema.NullOr(ChangesetId), // the changeset that applied it
    }),
  ),
  /** Meaning-based dedupe: kind + subject + intent. A duplicate open proposal is rejected with a pointer. */
  intentKey: Schema.String,
})
export type Proposal = typeof Proposal.Type
// #endregion proposal
